import React, { useEffect, useRef, useState } from "react";
import { Input, Button, Avatar, Tag, Tooltip } from "antd";
import {
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import trans from "../../utils";
import { C, S, R, E, T } from "./copilotTheme";

/* ─── motion: injected once, no extra deps ─────────────────────────────── */
const TYPING_STYLE = `
@keyframes copilot-dot-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
  40%           { transform: translateY(-4px); opacity: 1; }
}
.copilot-dot {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${C.inkFaint};
  margin: 0 2px;
  animation: copilot-dot-bounce 1.3s infinite ease-in-out;
}
.copilot-dot:nth-child(2) { animation-delay: 0.15s; }
.copilot-dot:nth-child(3) { animation-delay: 0.3s; }

@keyframes copilot-msg-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.copilot-msg { animation: copilot-msg-in 0.2s ease both; }
`;

function injectStyle() {
  if (document.getElementById("copilot-chat-style")) return;
  const el = document.createElement("style");
  el.id = "copilot-chat-style";
  el.textContent = TYPING_STYLE;
  document.head.appendChild(el);
}

/* ─── helpers (behavior unchanged) ─────────────────────────────────────── */
function isArabicText(text) {
  if (!text || typeof text !== "string") return false;
  const arabicChars = (text.match(/[\u0600-\u06FF]/g) || []).length;
  return arabicChars > text.length * 0.2;
}

function getDisplayText(message) {
  if (typeof message.content === "string") return message.content;
  if (!Array.isArray(message.content)) return "";
  return message.content
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("\n")
    .trim();
}

function isVisibleMessage(message) {
  if (!message || !message.content) return false;
  if (message.role === "user") return typeof message.content === "string";
  if (message.role === "assistant" || message.role === "model") {
    if (typeof message.content === "string") return true;
    return Array.isArray(message.content)
      ? message.content.some((b) => b.type === "text")
      : false;
  }
  return false;
}

/* ─── sub-components ───────────────────────────────────────────────────── */
function TypingIndicator() {
  return (
    <div
      className="copilot-msg"
      style={{ display: "flex", alignItems: "flex-end", gap: S.sm }}
    >
      <Avatar
        size={28}
        icon={<RobotOutlined />}
        style={{
          background: C.surface,
          color: C.accent,
          border: `1px solid ${C.line}`,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          padding: "10px 14px",
          borderRadius: `${R.sm}px ${R.md}px ${R.md}px ${R.md}px`,
          background: C.surface,
          border: `1px solid ${C.line}`,
          boxShadow: E.low,
          display: "flex",
          alignItems: "center",
          minHeight: 36,
        }}
      >
        <span className="copilot-dot" />
        <span className="copilot-dot" />
        <span className="copilot-dot" />
      </div>
    </div>
  );
}

function ChatHeader({ messageCount, onClear }) {
  return (
    <div
      style={{
        padding: `${S.md}px ${S.lg}px`,
        background: C.surface,
        borderBottom: `1px solid ${C.line}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: R.md,
            background: C.accentSoft,
            border: `1px solid ${C.accentLine}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RobotOutlined style={{ color: C.accent, fontSize: 17 }} />
        </div>
        <div>
          <div
            style={{
              color: C.ink,
              fontWeight: 600,
              fontSize: T.bodyLg,
              lineHeight: 1.25,
            }}
          >
            {trans("IR Copilot")}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: S.xs + 2,
              marginTop: 2,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: C.ok,
              }}
            />
            <span style={{ color: C.inkFaint, fontSize: T.meta }}>
              {trans("AI Assistant · ready")}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: S.sm }}>
        <Tag
          style={{
            margin: 0,
            fontSize: T.meta - 1,
            padding: "1px 8px",
            borderRadius: R.pill,
            background: C.surfaceSunken,
            border: `1px solid ${C.line}`,
            color: C.inkSoft,
            fontWeight: 600,
            letterSpacing: "0.04em",
          }}
        >
          {trans("Beta")}
        </Tag>
        {messageCount > 0 && (
          <Tooltip title={trans("Clear conversation")}>
            <Button
              type="text"
              size="small"
              icon={<ClearOutlined />}
              onClick={onClear}
              style={{ color: C.inkFaint }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: `${S.xxl + 8}px ${S.xxl}px`,
        textAlign: "center",
        gap: S.md,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: R.lg,
          background: C.accentSoft,
          border: `1px solid ${C.accentLine}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RobotOutlined style={{ fontSize: 24, color: C.accent }} />
      </div>
      <div style={{ fontWeight: 600, fontSize: T.bodyLg, color: C.ink }}>
        {trans("How can I help you today?")}
      </div>
      <div
        style={{
          fontSize: T.small,
          color: C.inkSoft,
          maxWidth: 280,
          lineHeight: 1.6,
        }}
      >
        {trans("Describe the IR you want to create and I'll fill in the details.")}
      </div>
      <div
        style={{
          marginTop: S.xs,
          padding: `${S.sm}px ${S.lg}px`,
          background: C.surface,
          border: `1px dashed ${C.line}`,
          borderRadius: R.md,
          fontSize: T.small,
          color: C.inkSoft,
          maxWidth: 300,
          lineHeight: 1.5,
        }}
      >
        {trans('"Create an IR for HDB project, civil, ground floor, ready tomorrow"')}
      </div>
    </div>
  );
}

/* ─── main component ───────────────────────────────────────────────────── */
export default function CopilotChat({ messages, loading, onSend, disabled }) {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    injectStyle();
  }, []);

  const visibleMessages = messages.filter(isVisibleMessage);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [visibleMessages.length, loading]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading || disabled) return;
    onSend(text);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    // Tell parent to reset (pass-through; parent owns state)
    onSend?.("__clear__");
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: C.surface,
        borderRadius: R.lg,
        border: `1px solid ${C.line}`,
        boxShadow: E.mid,
        overflow: "hidden",
      }}
    >
      <ChatHeader
        messageCount={visibleMessages.length}
        onClear={handleClear}
      />

      {/* ── Message list ── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: `${S.xl}px ${S.lg}px`,
          display: "flex",
          flexDirection: "column",
          gap: S.lg,
          background: C.surfaceMuted,
        }}
      >
        {visibleMessages.length === 0 && !loading && <EmptyState />}

        {visibleMessages.map((msg, idx) => {
          const isUser = msg.role === "user";
          const text = getDisplayText(msg);
          const rtl = isArabicText(text);
          const ts = msg.timestamp ? dayjs(msg.timestamp) : null;

          return (
            <div
              key={idx}
              className="copilot-msg"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
              }}
            >
              <span
                style={{
                  fontSize: T.meta,
                  color: C.inkFaint,
                  marginBottom: S.xs,
                  paddingInline: S.xs,
                  fontWeight: 500,
                }}
              >
                {isUser ? trans("You") : trans("Copilot")}
              </span>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: S.sm,
                  flexDirection: isUser ? "row-reverse" : "row",
                  maxWidth: "85%",
                }}
              >
                <Avatar
                  size={28}
                  icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                  style={{
                    flexShrink: 0,
                    background: isUser ? C.accent : C.surface,
                    color: isUser ? "#fff" : C.accent,
                    border: isUser ? "none" : `1px solid ${C.line}`,
                  }}
                />

                <div
                  dir={rtl ? "rtl" : "ltr"}
                  style={{
                    padding: "10px 14px",
                    borderRadius: isUser
                      ? `${R.md}px ${R.sm}px ${R.md}px ${R.md}px`
                      : `${R.sm}px ${R.md}px ${R.md}px ${R.md}px`,
                    background: isUser ? C.accent : C.surface,
                    color: isUser ? "#fff" : C.ink,
                    border: isUser ? "none" : `1px solid ${C.line}`,
                    boxShadow: isUser ? "none" : E.low,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: T.body,
                    lineHeight: 1.6,
                  }}
                >
                  {text}
                </div>
              </div>

              <span
                style={{
                  fontSize: T.meta - 1,
                  color: C.inkFaint,
                  marginTop: S.xs - 1,
                  paddingInline: 40,
                  textAlign: isUser ? "right" : "left",
                }}
              >
                {ts ? ts.format("HH:mm") : dayjs().format("HH:mm")}
              </span>
            </div>
          );
        })}

        {loading && <TypingIndicator />}
      </div>

      {/* ── Input row ── */}
      <div
        style={{
          padding: `${S.md}px ${S.md}px`,
          background: C.surface,
          borderTop: `1px solid ${C.line}`,
          display: "flex",
          alignItems: "flex-end",
          gap: S.sm,
        }}
      >
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={trans("Type a message... (Enter to send)")}
          autoSize={{ minRows: 1, maxRows: 5 }}
          disabled={disabled || loading}
          style={{
            resize: "none",
            borderRadius: R.md,
            fontSize: T.body,
            padding: "8px 14px",
            background: C.surfaceSunken,
            border: `1px solid ${C.line}`,
            boxShadow: "none",
          }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={!input.trim() || disabled}
          loading={loading}
          style={{
            width: 38,
            height: 38,
            minWidth: 38,
            flexShrink: 0,
            borderRadius: R.md,
            background: input.trim() ? C.accent : undefined,
            border: "none",
          }}
        />
      </div>
    </div>
  );
}